import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { VirtualVehicleVehicle } from '@/@types/VirtualVehicle';
import BulletSeparatedList from '../../../../styled-components/components/BulletSeparatedList';
import { Button } from '../../../../styled-components/components/Button';
import { Column, Columns } from '../../../../styled-components/components/Columns';
import {
  KeyValueList,
  KeyValueListItem,
  KeyValueListKey,
  KeyValueListValue,
} from '../../../../styled-components/components/KeyValueList';
import { LinkButton } from '../../../../styled-components/components/LinkButton';
import { SeparatorListItem } from '../../../../styled-components/components/SeparatorList';
import { StackSmall } from '../../../../styled-components/components/Stack';
import { Text } from '../../../../styled-components/components/Text';
import { VStack } from '../../../../styled-components/components/VStack';
import { currency } from '../../../../utils/currency';
import { PrettyNumber } from '../../../../utils/string-formatter';

interface TemplateItemProps {
  template: VirtualVehicleVehicle;
  onSelect: () => void;
}

export const TemplateItem = ({ template, onSelect }: TemplateItemProps) => {
  const { t } = useTranslation(['createVehiclePanel', 'common', 'glossary']);
  const [open, setOpen] = useState(false);

  const defaultItems = [
    { value: template.modelYear, label: t('modelYear', { ns: 'glossary' }) },
    { value: template.drivingWheel, label: t('drivingWheel', { ns: 'glossary' }) },
    {
      value: template.batteryCapacity && `${template.batteryCapacity}kWh`,
      label: t('batteryCapacity', { ns: 'glossary' }),
    },
  ];

  const extendedItems = [
    ...defaultItems,
    {
      value: `${template.enginePower}${t('enginePowerUnit', { ns: 'glossary' })}`,
      label: t('enginePower', { ns: 'glossary' }),
    },
    { value: template.chassis, label: t('chassisName', { ns: 'glossary' }) },
    { value: template.engineBaseType, label: t('engineBaseType', { ns: 'glossary' }) },
    { value: template.environmentClass, label: t('environmentClass', { ns: 'glossary' }) },
    { value: template.fuelType, label: t('fuelType', { ns: 'glossary' }) },
    { value: template.fuelTypes, label: t('fuelTypes', { ns: 'glossary' }) },
    { value: template.gearboxType, label: t('gearbox', { ns: 'glossary' }) },
    {
      value: template.manufacturerConfigurationId,
      label: t('manufacturerConfigurationId', { ns: 'glossary' }),
    },
    {
      value: currency(PrettyNumber(template.retailerPrice)),
      label: t('price', { ns: 'glossary' }),
    },
  ];

  return (
    <SeparatorListItem key={template.id}>
      <StackSmall>
        <Columns $spacing={2} $valign="center">
          <Column $grow>
            <VStack $spacing={0.5}>
              <Text $size="small">
                <Text as="span" $weight="bold">
                  {template.manufacturer}
                </Text>{' '}
                {(() => {
                  const modelWords = template.modelSeries
                    .split(' ')
                    .filter((word) => word.length > 0);
                  const salesWords = template.salesName
                    .split(' ')
                    .filter((word) => word.length > 0);
                  const uniqueWords = [...modelWords];
                  const uniqueWordsLower = modelWords.map((word) => word.toLowerCase());

                  salesWords.forEach((word) => {
                    const wordLower = word.toLowerCase();
                    if (!uniqueWordsLower.includes(wordLower)) {
                      uniqueWords.push(word);
                      uniqueWordsLower.push(wordLower);
                    }
                  });

                  return uniqueWords.join(' ');
                })()}
              </Text>
              <BulletSeparatedList
                size="small"
                tone="alt"
                items={defaultItems.map((item) => item.value)}
              />
            </VStack>
            <LinkButton
              $size="xsmall"
              title={
                open
                  ? t('showLessInformation', { ns: 'common' })
                  : t('showMoreInformation', { ns: 'common' })
              }
              onClick={() => setOpen(!open)}
            >
              {open
                ? t('showLessInformation', { ns: 'common' })
                : t('showMoreInformation', { ns: 'common' })}
            </LinkButton>
          </Column>
          <Column $noShrink>
            <Button
              $tone="primary"
              onClick={() => {
                onSelect();
              }}
              title={t('select', { ns: 'common' })}
            >
              {t('select', { ns: 'common' })}
            </Button>
          </Column>
        </Columns>
      </StackSmall>
      {open && (
        <StackSmall>
          <KeyValueList>
            {extendedItems
              .filter((item) => item.value)
              .map((item, idx) => (
                <KeyValueListItem key={idx}>
                  <KeyValueListKey>{item.label}</KeyValueListKey>
                  <KeyValueListValue>{item.value}</KeyValueListValue>
                </KeyValueListItem>
              ))}
          </KeyValueList>
        </StackSmall>
      )}
    </SeparatorListItem>
  );
};
